import { forwardRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { EyeOff, QrCode, RotateCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { BANNED_RETRY_LABEL, BannedRetryDialog } from '@/features/accounts/BannedRetryDialog';
import { HIDE_ACCOUNT_LABEL, HideAccountDialog } from '@/features/accounts/HideAccountDialog';
import { StatusDot } from '@/features/accounts/StatusDot';
import { QrRequiredDialog } from '@/features/accounts/QrRequiredDialog';
import { BAN_STORM_HELD_LABEL, isBanStormHeld } from '@/features/accounts/attention-labels';
import type { AccountColor } from '@/features/accounts/account-colors';
import {
  formatAccountLabel,
  isArchivedAccount,
  type Account,
} from '@/features/accounts/accounts.api';
import { useAccountUnreadSum } from '@/features/accounts/useAccountUnreadSum';
import { useArchiveAccount } from '@/features/accounts/useArchiveAccount';
import { useReconnectAccount } from '@/features/accounts/useReconnectAccount';
import { useRescanAccount } from '@/features/accounts/useRescanAccount';
import { useAuthStore } from '@/store/auth';
import { useUIStore } from '@/store/ui';
import { cn } from '@/lib/utils';

interface AccountRowProps {
  account: Account;
  /** This account's identity color (see `account-colors.ts`), resolved by
   *  the parent `AccountSidebar` from a single shared `useAccountColors()`
   *  call and passed down — every row calling the hook itself would mount a
   *  fresh query observer per row, which is needless churn on top of the
   *  sidebar's own already-cache-warm `useAccounts()` subscription. */
  color: AccountColor;
}

/**
 * One account row in the sidebar.
 *
 * Layout: [StatusDot] [color swatch] [phone-derived label (formatAccountLabel)
 * + optional TEST badge] [QR button] [admin: 从账号池隐藏 on a non-connected
 * row]. The QR button only appears when `status ===
 * 'qr_required'`; its click is stopPropagation'd so it opens the
 * placeholder dialog instead of toggling row selection. Selected rows get a
 * left-border accent and a subtle bg tint — matches the conversation-row
 * treatment M5-T4 will use.
 *
 * The `StatusDot` and the color swatch are deliberately separate and
 * adjacent: the dot means CONNECTION STATUS (shared vocabulary across every
 * account — green/amber/orange/rose) while the swatch is a stable per-account
 * IDENTITY color (see `account-colors.ts`) used to visually tie this row to
 * the account's badge on aggregate-mode conversation rows.
 *
 * The row is a `<div role="button">` rather than a real `<button>` so we can
 * nest the QR action button inside it without violating the
 * no-interactive-inside-interactive rule. Forwarded ref lets the parent
 * QrAttentionBanner scroll the first `qr_required` row into view.
 */
export const AccountRow = forwardRef<HTMLDivElement, AccountRowProps>(
  ({ account, color }, ref) => {
    const selectedAccountId = useUIStore((s) => s.selectedAccountId);
    const setSelectedAccountId = useUIStore((s) => s.setSelectedAccountId);
    const isAdmin = useAuthStore((s) => s.agent?.role) === 'admin';
    const [dialogOpen, setDialogOpen] = useState(false);
    // Whether the admin's first 重新扫码 click is awaiting the explicit
    // 确认 / 取消 second step (ManageAgentsDialog pattern — no timers; the
    // confirm state only changes on a click).
    const [confirmingRescan, setConfirmingRescan] = useState(false);
    // The banned-row 重试连接（保留凭据） confirm dialog.
    const [retryOpen, setRetryOpen] = useState(false);
    // The 从账号池隐藏 confirm dialog.
    const [hideOpen, setHideOpen] = useState(false);
    const rescan = useRescanAccount();
    const reconnect = useReconnectAccount();
    const archive = useArchiveAccount();

    // Phone-derived display name — the ONLY way an account is named anywhere
    // in the console (visible text AND accessible names alike; the raw
    // auto-generated external id must never surface to an operator).
    const label = formatAccountLabel(account);
    const isSelected = selectedAccountId === account.id;
    const needsQr = account.status === 'qr_required';
    // A `connecting` or `qr_required` account is the one that just paired and
    // may be stuck at a Baileys 515 ("restart required after pairing"), or is
    // otherwise mid-handshake. For an ADMIN we offer a surgical 重连 that
    // finalizes/re-establishes JUST this account (gateway untouched, no other
    // account blinks, no ~20s hub inbound-loss window) while PRESERVING its
    // credentials — distinct from the banned-row 重新扫码, which wipes creds
    // and re-pairs. Non-admins see nothing.
    const canReconnect =
      isAdmin && (account.status === 'connecting' || account.status === 'qr_required');
    // A banned account shows an unmistakable red verdict badge where
    // `qr_required` would show the QR button. Mutually exclusive with
    // `needsQr`: the backend never reports both states at once. For an ADMIN
    // the badge additionally carries two actions: 重新扫码 (re-scan) — the
    // repair that wipes the dead credentials and re-pairs the number (on
    // success the backend flips the row to `qr_required`, so this row
    // re-renders into the existing QR affordance) — and 重试连接（保留凭据）,
    // the same `/reconnect` as above, which the hub accepts on a banned row as
    // "retry with the preserved credentials" (re-enable + start, no logout),
    // behind a confirm dialog. Non-admins see only the read-only badge.
    const isBanned = account.status === 'banned';
    // A ban HELD by the hub's 403-storm guard was NOT stopped (the number
    // keeps retrying, `enabled:true`): its badge says so instead of the plain
    // 疑似封号, and its tooltip asks for an admin now.
    const stormHeld = isBanStormHeld(account);
    const bannedActionPending = rescan.isPending || reconnect.isPending;
    const unreadSum = useAccountUnreadSum(account.id);
    // Admin-only 从账号池隐藏 for a dead / duplicate row (an old number
    // re-paired under a new id, a never-paired 待配对 create). NEVER on a
    // `connected` row — the hub refuses it (409) and a live number must stay
    // visible. The hub side is DB-only (no gateway call); the confirm dialog
    // says so. Hidden while the banned row's re-scan confirm is showing.
    const canHide =
      isAdmin && account.status !== 'connected' && !isArchivedAccount(account);

    // The row left `banned` while the confirm was open (a poll / WS patch):
    // drop it, so a later ban cannot re-open a stale dialog by itself.
    if (retryOpen && !isBanned) setRetryOpen(false);
    // Same for the hide confirm when the row went `connected` meanwhile.
    if (hideOpen && !canHide) setHideOpen(false);

    function handleSelect(): void {
      setSelectedAccountId(account.id);
    }

    function handleRescanRequest(e: MouseEvent<HTMLButtonElement>): void {
      // Don't bubble — clicking the action must not select the row.
      e.stopPropagation();
      setConfirmingRescan(true);
    }

    function handleRescanCancel(e: MouseEvent<HTMLButtonElement>): void {
      e.stopPropagation();
      setConfirmingRescan(false);
    }

    function handleRescanConfirm(e: MouseEvent<HTMLButtonElement>): void {
      e.stopPropagation();
      // Reset the confirm state whether the POST succeeds or fails — on
      // success the row flips to `qr_required` (badge unmounts); on failure
      // the toast explains why and the row returns to its plain action for a
      // deliberate retry.
      rescan.mutate(account.id, { onSettled: () => setConfirmingRescan(false) });
    }

    function handleRowKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleSelect();
      }
    }

    function handleQrClick(e: MouseEvent<HTMLButtonElement>): void {
      // Don't bubble — clicking the QR button opens the dialog, NOT the row.
      e.stopPropagation();
      setDialogOpen(true);
    }

    function handleReconnectClick(e: MouseEvent<HTMLButtonElement>): void {
      // Don't bubble — clicking the action must not select the row. Reconnect
      // is non-destructive (creds preserved), so there is no confirm step; we
      // just disable the button while the mutation is in flight.
      e.stopPropagation();
      reconnect.mutate(account.id);
    }

    function handleRetryRequest(e: MouseEvent<HTMLButtonElement>): void {
      // Don't bubble — opening the confirm must not select the row.
      e.stopPropagation();
      setRetryOpen(true);
    }

    function handleHideRequest(e: MouseEvent<HTMLButtonElement>): void {
      // Don't bubble — opening the confirm must not select the row.
      e.stopPropagation();
      setHideOpen(true);
    }

    function handleHideConfirm(): void {
      // Close on either outcome: success writes the archived row into the
      // cache (the row leaves the pool); a failure — e.g. the hub's 409 for a
      // row that went `connected` — toasts the hub's detail verbatim.
      archive.mutate(account.id, { onSettled: () => setHideOpen(false) });
    }

    function handleRetryConfirm(): void {
      // Close on either outcome: success toasts 已触发重连 (the status lands
      // via the poll / WS); a failure — e.g. an older hub's 409 for `banned` —
      // toasts the hub's detail verbatim (`useReconnectAccount`) and the row
      // stays as it was.
      reconnect.mutate(account.id, { onSettled: () => setRetryOpen(false) });
    }

    return (
      <>
        <div
          ref={ref}
          role="button"
          tabIndex={0}
          onClick={handleSelect}
          onKeyDown={handleRowKeyDown}
          aria-pressed={isSelected}
          // Plain DOM lookup hook for `Layout.tsx`'s `FleetStatusBanner` click
          // handler ("expands the rail and scrolls to the first critical
          // row" — spec 01 §3.5): `FleetStatusBanner` lives outside
          // `AccountSidebar` (this row's actual parent, which T1-Fb does not
          // own) and cannot reach a ref threaded through it, so it targets
          // this attribute directly via `document.querySelector` instead.
          // Independent of the `ref` prop above (that one is AccountSidebar's
          // own first-qr/first-ban scroll anchor).
          data-account-row-id={account.id}
          // Neumorphic: raised card by default; the selected row is pressed
          // INTO the panel (inset) with a 3px green accent bar. Shadow sets
          // are mutually exclusive per state so tailwind-merge never has to
          // arbitrate between the raised and inset presets.
          className={cn(
            'neu-pressable flex w-full cursor-pointer items-center gap-2.5 rounded-[13px] border-l-[3px] border-transparent px-3 py-2.5 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
            !isSelected &&
              'shadow-neu-raised hover:shadow-neu-raised-hover active:shadow-neu-inset-press',
            isSelected && 'border-l-primary bg-[var(--raised-bg)] shadow-neu-inset',
          )}
        >
          <StatusDot
            status={account.status}
            statusSince={account.status_since}
            statusDetail={account.status_detail}
          />
          <span
            aria-hidden="true"
            data-testid="account-color-swatch"
            className={cn('size-2.5 shrink-0 rounded-[4px]', color.swatch)}
          />
          <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-neu-sub">
            {label}
            {account.is_test && (
              <span className="ml-2 inline-block rounded-sm border border-muted-foreground/30 px-1 py-0.5 text-[10px] font-semibold uppercase leading-none text-muted-foreground">
                Test
              </span>
            )}
          </span>
          {unreadSum > 0 && (
            <span
              aria-label={`${unreadSum} unread`}
              className="neu-gradient flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-extrabold leading-none text-white shadow-neu-flat"
            >
              {unreadSum > 99 ? '99+' : unreadSum}
            </span>
          )}
          {/* QR pairing is admin-only (mirrors the backend `require_admin`
              on GET /accounts/{id}/qr): scanning a QR links a WhatsApp device,
              an account-lifecycle op. A plain agent sees only the status dot. */}
          {needsQr && isAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={handleQrClick}
                  aria-label={`Re-scan QR for ${label}`}
                  className="size-7 shrink-0 text-orange-600 hover:bg-orange-100 hover:text-orange-700"
                >
                  <QrCode className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Re-scan QR code</TooltipContent>
            </Tooltip>
          )}
          {canReconnect && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  disabled={reconnect.isPending}
                  onClick={handleReconnectClick}
                  aria-label={`重连 ${label}`}
                  className="size-7 shrink-0 text-sky-600 hover:bg-sky-100 hover:text-sky-700"
                >
                  <RotateCw className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>完成配对/重连（不清凭据）</TooltipContent>
            </Tooltip>
          )}
          {isBanned && (
            <div className="flex shrink-0 items-center gap-1.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* Red verdict pill — the always-present ban label. For an
                      admin the 重新扫码 / 重试连接 actions sit beside it; for a
                      non-admin it stands alone (the tooltip points them at an
                      admin). */}
                  <span
                    data-testid="account-banned-badge"
                    role="status"
                    aria-label={`${label} ${stormHeld ? BAN_STORM_HELD_LABEL : '疑似封号'}`}
                    className="shrink-0 rounded-sm bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-white shadow-neu-flat"
                  >
                    {stormHeld ? BAN_STORM_HELD_LABEL : '疑似封号'}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {stormHeld
                    ? isAdmin
                      ? '403 风暴中未停用：该号仍在重试，需要管理员立即处理'
                      : '403 风暴中未停用：该号仍在重试 · 请立即通知管理员'
                    : isAdmin
                      ? '疑似封号 · 可重试连接（保留凭据）或重新扫码'
                      : '疑似封号 · 请联系管理员处理'}
                </TooltipContent>
              </Tooltip>

              {/* Admin-only re-scan action. Two-step confirm: the first click
                  swaps the button into explicit 确认 / 取消 controls plus a
                  helper line — a re-scan wipes the number's old login
                  credentials, so it must be deliberate. */}
              {isAdmin && !confirmingRescan && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={bannedActionPending}
                      onClick={handleRescanRequest}
                      aria-label={`重新扫码 ${label}`}
                      className="h-6 shrink-0 px-2 text-[11px] font-bold text-orange-600 hover:bg-orange-100 hover:text-orange-700"
                    >
                      重新扫码
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>将清除旧登录凭据并重新配对</TooltipContent>
                </Tooltip>
              )}

              {/* Admin-only retry with the preserved credentials — opens the
                  BannedRetryDialog confirm (rendered outside this row, below).
                  Hidden while the re-scan confirm is showing. */}
              {isAdmin && !confirmingRescan && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      disabled={bannedActionPending}
                      onClick={handleRetryRequest}
                      aria-label={`${BANNED_RETRY_LABEL} ${label}`}
                      className="size-6 shrink-0 text-sky-600 hover:bg-sky-100 hover:text-sky-700"
                    >
                      <RotateCw className="size-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{BANNED_RETRY_LABEL}</TooltipContent>
                </Tooltip>
              )}

              {isAdmin && confirmingRescan && (
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <p className="text-[10px] leading-tight text-muted-foreground">
                    将清除旧登录凭据并重新配对
                  </p>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={rescan.isPending}
                      onClick={handleRescanCancel}
                      aria-label={`取消重新扫码 ${label}`}
                      className="h-6 px-2 text-[11px]"
                    >
                      取消
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      disabled={rescan.isPending}
                      onClick={handleRescanConfirm}
                      aria-label={`确认重新扫码 ${label}`}
                      className="h-6 px-2 text-[11px]"
                    >
                      确认
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {canHide && !confirmingRescan && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  disabled={archive.isPending}
                  onClick={handleHideRequest}
                  aria-label={`${HIDE_ACCOUNT_LABEL} ${label}`}
                  className="size-7 shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <EyeOff className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{HIDE_ACCOUNT_LABEL}（不影响网关，可恢复）</TooltipContent>
            </Tooltip>
          )}
        </div>
        {/* Conditional mount: the dialog calls `useAccountQr` (useQuery), and
            its observer combines with `useAccountUnreadSum`'s QueryCache
            subscription. Mounting only when actually opened means no
            useQuery observer exists in the steady state, and the dialog
            tear-down on close cleans up everything. */}
        {needsQr && dialogOpen && isAdmin && (
          <QrRequiredDialog
            account={account}
            open={dialogOpen}
            onOpenChange={setDialogOpen}
          />
        )}
        {/* OUTSIDE the row: a portal still bubbles React events through its
            owner tree, so inside the row a dialog click would select the row
            and an Enter on 确认重试 would hit the row's key handler. */}
        {isBanned && isAdmin && (
          <BannedRetryDialog
            open={retryOpen}
            onOpenChange={setRetryOpen}
            accountLabel={label}
            onConfirm={handleRetryConfirm}
            pending={reconnect.isPending}
          />
        )}
        {/* Outside the row for the same reason as BannedRetryDialog. */}
        {canHide && (
          <HideAccountDialog
            open={hideOpen}
            onOpenChange={setHideOpen}
            accountLabel={label}
            onConfirm={handleHideConfirm}
            pending={archive.isPending}
          />
        )}
      </>
    );
  },
);
AccountRow.displayName = 'AccountRow';
